/**
 * Rolling performance metrics: render fps, inference time, inference rate and
 * grab→render latency. Feeds the quality governor, the debug panel and the
 * analytics events (average fps, tracking failure rate).
 */
class Ring {
  private readonly buf: Float64Array;
  private idx = 0;
  private count = 0;
  constructor(size: number) {
    this.buf = new Float64Array(size);
  }
  push(v: number): void {
    this.buf[this.idx] = v;
    this.idx = (this.idx + 1) % this.buf.length;
    if (this.count < this.buf.length) this.count++;
  }
  mean(): number {
    if (this.count === 0) return 0;
    let s = 0;
    for (let i = 0; i < this.count; i++) s += this.buf[i]!;
    return s / this.count;
  }
  p95(): number {
    if (this.count === 0) return 0;
    const arr = Array.from(this.buf.subarray(0, this.count)).sort((a, b) => a - b);
    return arr[Math.min(arr.length - 1, Math.floor(arr.length * 0.95))]!;
  }
  get size(): number {
    return this.count;
  }
  clear(): void {
    this.idx = 0;
    this.count = 0;
  }
}

export interface PerfSnapshot {
  fps: number;
  inferMs: number;
  inferP95Ms: number;
  inferPerSecond: number;
  latencyMs: number;
  droppedFrames: number;
}

export class PerfMonitor {
  private readonly frameDt = new Ring(60);
  private readonly infer = new Ring(30);
  private readonly latency = new Ring(30);
  private readonly inferTimes: number[] = [];
  private lastRender = -1;
  private dropped = 0;
  /** Session-level accumulators for analytics. */
  readonly session = { renderFrames: 0, inferences: 0, fpsSum: 0, fpsSamples: 0, startedAt: 0 };

  markRender(nowMs: number): void {
    if (this.lastRender >= 0) {
      const dt = nowMs - this.lastRender;
      this.frameDt.push(dt);
      if (dt > 40) this.dropped++;
    }
    this.lastRender = nowMs;
    this.session.renderFrames++;
    if (this.session.startedAt === 0) this.session.startedAt = nowMs;
    if (this.session.renderFrames % 60 === 0) {
      this.session.fpsSum += this.fps;
      this.session.fpsSamples++;
    }
  }

  markInference(inferMs: number, latencyMs: number, nowMs: number): void {
    this.infer.push(inferMs);
    this.latency.push(latencyMs);
    this.inferTimes.push(nowMs);
    while (this.inferTimes.length > 0 && nowMs - this.inferTimes[0]! > 1000) this.inferTimes.shift();
    this.session.inferences++;
  }

  /** Forgets the recent frame intervals (after a hidden-tab spell, where frames are ticked at 1 Hz). */
  resetTiming(): void {
    this.frameDt.clear();
    this.lastRender = -1;
  }

  /** Forgets the recent inference times (start-up: the first, cold inferences say nothing about the device). */
  resetInference(): void {
    this.infer.clear();
    this.latency.clear();
    this.inferTimes.length = 0;
  }

  get fps(): number {
    const m = this.frameDt.mean();
    return m > 0 ? 1000 / m : 0;
  }

  get inferMs(): number {
    return this.infer.mean();
  }

  get latencyMs(): number {
    return this.latency.mean();
  }

  snapshot(): PerfSnapshot {
    return {
      fps: this.fps,
      inferMs: this.infer.mean(),
      inferP95Ms: this.infer.p95(),
      inferPerSecond: this.inferTimes.length,
      latencyMs: this.latency.mean(),
      droppedFrames: this.dropped,
    };
  }

  get sessionAverageFps(): number {
    return this.session.fpsSamples > 0 ? this.session.fpsSum / this.session.fpsSamples : this.fps;
  }
}
